import { useEffect, useMemo } from "react";
import type { AppStore } from "@/store/AppStore";
import type { SmartCardRow } from "@/domains/tanstack-db";
import { toTypedSmartCard } from "@/components/heads-up/cards/registry";
import { SmartCardKind } from "@/components/heads-up/heads-up.types";
import type { HeadsUpDataSourceResult } from "./use-data-source";

interface UseRelatedEntitiesArgs {
  store: AppStore;
  activeNoteId: string | null;
  displayedCards: SmartCardRow[];
  ensureNoteHydrated: HeadsUpDataSourceResult["actions"]["ensureNoteHydrated"];
}

export interface RelatedEntityIds {
  noteIds: string[];
  collectionIds: string[];
}

export function useRelatedEntities({
  store,
  activeNoteId,
  displayedCards,
  ensureNoteHydrated,
}: UseRelatedEntitiesArgs): RelatedEntityIds {
  const relatedEntities = useMemo<RelatedEntityIds>(() => {
    const noteIds = new Set<string>();
    const collectionIds = new Set<string>();

    if (activeNoteId) noteIds.add(activeNoteId);

    for (const card of displayedCards) {
      const typed = toTypedSmartCard(card);
      if (!typed) continue;

      switch (typed.kind) {
        case SmartCardKind.NoteList:
        case SmartCardKind.MentionedByNotesList:
          typed.value.items.forEach(item => {
            if (item.note_id) noteIds.add(item.note_id);
          });
          break;
        case SmartCardKind.EventTimeline:
          typed.value.items.forEach(item => {
            if (item.linked_note_id) noteIds.add(item.linked_note_id);
          });
          break;
        case SmartCardKind.CollectionList:
          typed.value.items.forEach(item => {
            if (item.collection_id) collectionIds.add(item.collection_id);
          });
          break;
        default:
          break;
      }
    }

    return {
      noteIds: Array.from(noteIds).sort(),
      collectionIds: Array.from(collectionIds).sort(),
    };
  }, [activeNoteId, displayedCards]);

  useEffect(() => {
    if (!activeNoteId) return;
    ensureNoteHydrated(activeNoteId).catch(() => {});
  }, [activeNoteId, ensureNoteHydrated]);

  useEffect(() => {
    relatedEntities.noteIds.forEach(id => {
      ensureNoteHydrated(id).catch(() => {});
    });
  }, [ensureNoteHydrated, relatedEntities.noteIds]);

  useEffect(() => {
    relatedEntities.collectionIds.forEach(id => {
      void store.collections.getAsync(id).catch(() => {});
    });
  }, [relatedEntities.collectionIds, store.collections]);

  return relatedEntities;
}
